import React, { useContext } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Box,
  Typography
} from "@mui/material";
import {
  BRAND_COLOR,
  BRAND_HOVER,
  KivuI18nContext,
  localizedPath,
  useKivunovaTranslation
} from "@kivunova/kivufrontendcommon";
import { useNavigate } from "react-router-dom";
import { ROUTE_SUBSCRIPTIONS } from "../../constants/routes";
import { dialogTitleCenterSx } from "../common/dialogTitleCenterSx";

export type ExistingSubscriptionKind = "ACTIVE" | "PENDING" | "BOTH";

const MODAL_MAX_WIDTH_PX = 560;

interface PlansExistingSubscriptionModalProps {
  open: boolean;
  onClose: () => void;
  kind: ExistingSubscriptionKind;
}

const PlansExistingSubscriptionModal: React.FC<
  PlansExistingSubscriptionModalProps
> = ({ open, onClose, kind }) => {
  const { t } = useKivunovaTranslation();
  const navigate = useNavigate();
  const { language, defaultLang } = useContext(KivuI18nContext) || {};

  const body =
    kind === "ACTIVE"
      ? t(
          "plans-existing-subscription-body-active",
          "You already have an active subscription. Cancel it from Subscriptions before you can choose a new plan."
        )
      : kind === "PENDING"
        ? t(
            "plans-existing-subscription-body-pending",
            "You already have a subscription pending payment. Cancel it from Subscriptions before you can choose a new plan."
          )
        : t(
            "plans-existing-subscription-body-both",
            "You already have an active or pending subscription. Cancel existing subscriptions from Subscriptions before you can choose a new plan."
          );

  const goToSubscriptions = () => {
    onClose();
    navigate(localizedPath(ROUTE_SUBSCRIPTIONS, language, defaultLang));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      aria-labelledby="plans-existing-subscription-title"
      PaperProps={{
        sx: {
          width: "100%",
          maxWidth: MODAL_MAX_WIDTH_PX,
          mx: 2,
          borderRadius: 3,
          overflow: "hidden",
          boxShadow: "0 24px 48px rgba(46, 31, 92, 0.16)"
        }
      }}
    >
      <Box
        sx={{
          height: 4,
          background:
            "linear-gradient(90deg, #667eea 0%, #764ba2 35%, #6f49c9 65%, #5b36b0 100%)"
        }}
      />
      <DialogTitle
        id="plans-existing-subscription-title"
        sx={{
          ...dialogTitleCenterSx,
          px: { xs: 2.5, sm: 3 },
          pt: 3,
          pb: 1
        }}
      >
        <Typography
          variant="h6"
          component="span"
          fontWeight={700}
          sx={{
            letterSpacing: "-0.02em",
            color: "text.primary",
            fontSize: { xs: "1.15rem", sm: "1.25rem" }
          }}
        >
          {t(
            "plans-existing-subscription-title",
            "Subscription already in progress"
          )}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, pt: 0.5, pb: 1 }}>
        <Typography
          variant="body1"
          sx={{ color: "text.secondary", lineHeight: 1.65 }}
        >
          {body}
        </Typography>
      </DialogContent>
      <Box
        sx={{
          px: { xs: 2.5, sm: 3 },
          pb: 3,
          pt: 1.5,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 1.25,
          width: "100%"
        }}
      >
        <Button
          variant="contained"
          fullWidth
          onClick={goToSubscriptions}
          sx={{
            maxWidth: 400,
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 2,
            py: 1.35,
            backgroundColor: BRAND_COLOR,
            boxShadow: "0 4px 14px rgba(111, 73, 201, 0.35)",
            "&:hover": {
              backgroundColor: BRAND_HOVER,
              boxShadow: "0 8px 20px rgba(111, 73, 201, 0.4)"
            }
          }}
        >
          {t(
            "plans-existing-subscription-go-to-subscriptions",
            "Go to subscriptions"
          )}
        </Button>
        <Button
          variant="outlined"
          fullWidth
          onClick={onClose}
          sx={{
            maxWidth: 400,
            textTransform: "none",
            fontWeight: 600,
            borderRadius: 2,
            borderColor: "divider",
            color: "text.primary",
            py: 1.2
          }}
        >
          {t("plans-existing-subscription-close", "Close")}
        </Button>
      </Box>
    </Dialog>
  );
};

export default PlansExistingSubscriptionModal;
